<script lang="ts">
  import { api, ApiError } from "$shared/api";
  import { addToast } from "$shared/toasts.svelte";
  import { getOpenCounter } from "$shared/feedback.svelte";

  // The public SPA hides the floating pill (modal opens via openFeedback()
  // callers like BetaBanner); the admin SPA keeps it as its only launcher.
  let { showPill = true }: { showPill?: boolean } = $props();

  let isOpen = $state(false);
  let body = $state("");
  let submitting = $state(false);
  let lastCounterSeen = 0;
  let bodyEl = $state<HTMLTextAreaElement>();

  // Focus the textarea whenever the modal opens (runs post-render, so the
  // element is bound; the HTML autofocus attribute is unreliable on
  // dynamically-inserted nodes).
  $effect(() => {
    if (isOpen) bodyEl?.focus();
  });

  // Re-open whenever the module-level counter ticks.
  $effect(() => {
    const c = getOpenCounter();
    if (c !== lastCounterSeen) {
      lastCounterSeen = c;
      isOpen = true;
    }
  });

  function close(): void {
    isOpen = false;
    body = "";
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;

    submitting = true;
    try {
      await api.post<{ id: number }>("/feedback", {
        body: trimmed,
        url: typeof window !== "undefined" ? window.location.href : null,
      });
      addToast("Thanks — we read every one.", "submit");
      close();
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 429
          ? "Too many submissions — please try again later."
          : "Could not send feedback. Please try again.";
      addToast(msg, "error");
    } finally {
      submitting = false;
    }
  }
</script>

{#if showPill}
  <button
    type="button"
    class="feedback-pill"
    aria-label="Send Feedback"
    onclick={() => (isOpen = true)}
  >
    Send Feedback
  </button>
{/if}

{#if isOpen}
  <div
    class="feedback-backdrop"
    role="dialog"
    aria-modal="true"
    aria-labelledby="feedback-title"
    tabindex="-1"
    onclick={(e) => {
      if (e.target === e.currentTarget) close();
    }}
    onkeydown={(e) => {
      if (e.key === "Escape") close();
    }}
  >
    <form class="feedback-modal" onsubmit={submit}>
      <h2 id="feedback-title">Send Feedback</h2>
      <p class="feedback-help">
        Anything — a bug, an idea, a confusing screen. We read everything.
      </p>
      <textarea
        bind:this={bodyEl}
        bind:value={body}
        placeholder="What's on your mind?"
        rows="6"
        required
      ></textarea>
      <div class="feedback-actions">
        <button type="button" class="btn-secondary" onclick={close}
          >Cancel</button
        >
        <button
          type="submit"
          class="btn-primary"
          disabled={submitting || body.trim().length === 0}
        >
          {submitting ? "Sending…" : "Send"}
        </button>
      </div>
    </form>
  </div>
{/if}

<style>
  .feedback-pill {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 50;
    padding: 10px 16px;
    border: none;
    border-radius: 999px;
    background: linear-gradient(135deg, #c93494, #5c2ba6);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.2px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
    cursor: pointer;
  }
  .feedback-pill:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  /* Mobile bottom tab bar lives at bottom: 0; lift the pill above it so it
     doesn't intercept clicks on the rightmost tabs (e.g. Account). */
  @media (max-width: 767px) {
    .feedback-pill {
      bottom: 80px;
    }
  }

  .feedback-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    padding: 16px;
  }

  .feedback-modal {
    background: #16213e;
    color: #e0e0e0;
    border: 1px solid #2d2d4e;
    border-radius: 12px;
    padding: 24px;
    width: 100%;
    max-width: 480px;
    box-shadow: 0 20px 48px rgba(0, 0, 0, 0.5);
  }
  .feedback-modal h2 {
    margin: 0 0 6px;
    font-size: 18px;
    color: #fff;
  }
  .feedback-help {
    margin: 0 0 16px;
    color: #aab;
    font-size: 14px;
  }
  .feedback-modal textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 12px;
    border: 1px solid #2d2d4e;
    border-radius: 8px;
    background: #0d1326;
    color: #fff;
    font: inherit;
    resize: vertical;
  }
  .feedback-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
  }
  .feedback-actions button {
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    border: none;
  }
  .btn-secondary {
    background: transparent;
    color: #aab;
    border: 1px solid #2d2d4e !important;
  }
  .btn-primary {
    background: linear-gradient(135deg, #c93494, #5c2ba6);
    color: #fff;
  }
  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
